import { useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { MoveHorizontal, MapPin, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CASE_STUDIES } from "@/data/caseStudies";

const BEFORE_IMG =
  "https://images.unsplash.com/photo-1659633874076-82f57ba8b486?crop=entropy&cs=srgb&fm=jpg&q=85";
const CURRENT_IMG =
  "https://images.unsplash.com/photo-1748063578185-3d68121b11ff?crop=entropy&cs=srgb&fm=jpg&q=85";

const GALLERY = CASE_STUDIES.map((c) => ({
  slug: c.slug,
  url: c.hero.url,
  title: c.title,
  location: c.location,
  category: c.category,
}));

const CompareSlider = () => {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);

  const update = useCallback((clientX) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  }, []);

  return (
    <div
      ref={ref}
      data-testid="before-current-slider"
      className="relative aspect-[4/3] w-full cursor-ew-resize touch-none select-none overflow-hidden border border-[#C5A059]/25 sm:aspect-[16/9]"
      onPointerDown={(e) => {
        dragging.current = true;
        ref.current.setPointerCapture(e.pointerId);
        update(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && update(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
    >
      <img
        src={CURRENT_IMG}
        alt="Current — property transformation underway with scaffolding, Essex"
        className="absolute inset-0 h-full w-full object-cover"
        draggable="false"
      />
      <span className="absolute right-4 top-4 bg-[#C5A059] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-[#050B14]">
        Current
      </span>
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img
          src={BEFORE_IMG}
          alt="Before — existing garage buildings prior to works, Essex"
          className="absolute inset-0 h-full w-full object-cover"
          draggable="false"
        />
        <span className="absolute left-4 top-4 border border-white/40 bg-[#050B14]/80 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-white backdrop-blur-sm">
          Before
        </span>
      </div>
      <div className="absolute inset-y-0 z-10" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -left-px w-0.5 bg-[#C5A059] shadow-[0_0_20px_rgba(197,160,89,0.7)]" />
        <button
          data-testid="slider-handle"
          role="slider"
          aria-label="Drag to compare before and current"
          aria-valuenow={Math.round(pos)}
          aria-valuemin={0}
          aria-valuemax={100}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") setPos((p) => Math.max(4, p - 4));
            if (e.key === "ArrowRight") setPos((p) => Math.min(96, p + 4));
          }}
          onPointerDown={(e) => e.stopPropagation()}
          className="absolute top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-[#C5A059] bg-[#050B14]/90 text-[#C5A059] shadow-[0_0_30px_rgba(197,160,89,0.4)] backdrop-blur-sm"
        >
          <MoveHorizontal size={22} />
        </button>
      </div>
    </div>
  );
};

export const Portfolio = () => (
  <section id="projects" data-testid="projects-section" className="relative bg-[#050B14] py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="mb-14 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
        <div>
          <div className="mb-6 flex items-center gap-4">
            <span className="h-px w-12 bg-gradient-to-r from-[#C5A059] to-transparent" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#C5A059]">Our Work</span>
          </div>
          <h2 className="font-serif text-3xl font-light uppercase leading-snug tracking-tight text-white sm:text-4xl lg:text-5xl">
            Property Transformation <span className="gold-text-gradient">— Essex</span>
          </h2>
        </div>
        <p className="max-w-sm text-sm font-light leading-relaxed text-slate-400">
          From existing structures to professionally managed property transformations.
          Drag the handle to compare.
        </p>
      </Reveal>

      <Reveal delay={0.1}>
        <CompareSlider />
        <div className="mt-8 flex flex-col items-start justify-between gap-5 sm:flex-row sm:items-center">
          <p className="max-w-lg text-base font-light text-slate-300">
            From existing structures to professionally managed property transformations.
          </p>
          <Link
            to="/case-studies/the-essex-transformation"
            data-testid="view-project-button"
            className="group border border-[#C5A059]/60 px-8 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C5A059] transition-all duration-300 hover:bg-[#C5A059] hover:text-[#050B14]"
          >
            View Project
          </Link>
        </div>
      </Reveal>

      <Reveal className="mt-20" delay={0.1}>
        <div className="mb-8 flex items-end justify-between">
          <h3 className="font-serif text-2xl font-light uppercase tracking-wide text-white">
            Case <span className="gold-text-gradient">Studies</span>
          </h3>
          <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Select a project to explore</span>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {GALLERY.map((g, i) => (
          <Reveal key={g.title} delay={i * 0.1}>
            <Link
              to={`/case-studies/${g.slug}`}
              data-testid={`project-card-${g.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group relative block aspect-[3/4] overflow-hidden border border-[#C5A059]/15 transition-colors duration-500 hover:border-[#C5A059]/50"
            >
              <img
                src={g.url}
                alt={`${g.title} — ${g.category}, ${g.location}`}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-[#050B14]/20 to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-95" />
              <div className="absolute inset-x-0 bottom-0 p-5">
                <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C5A059]">{g.category}</span>
                <h3 className="mt-2 font-serif text-xl font-medium text-white">{g.title}</h3>
                <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-400">
                  <MapPin size={12} className="text-[#C5A059]" /> {g.location}
                </p>
                <span className="mt-3 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C5A059] opacity-0 transition-all duration-500 group-hover:opacity-100">
                  View Case Study <ArrowUpRight size={13} />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
        </div>
      </Reveal>
    </div>
  </section>
);
